import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/client';
import Button from '../../../../../ui/canon/Button';

// RJ Seller Visibility Workflow — one-shot intro modal.
// Fires the first time a succession user lands on a page that mounts this
// component (LaunchPad or /succession/settings), after the tenant has enabled
// `rj_seller_visibility`.
//
// Dismissal is persisted BOTH server-side (users.data
// rj_visibility_first_run_dismissed_at, via rj_dismiss_first_run) and in
// localStorage. The server copy is authoritative; localStorage is only a fast
// path so the modal does not flash before the user query resolves.
//
// It used to be localStorage ONLY, which meant a new browser, a new device,
// cleared storage or private mode re-prompted a seller who had already chosen
// "Not now" — reported by Paige 2026-09-01.
//
// See docs/plans/2026-07-17-001-feat-rj-seller-visibility-workflow-plan.md
const STORAGE_KEY_PREFIX = 'rj_visibility_first_run_shown_v1_';

const RJ_DISMISS_FIRST_RUN = gql`
  mutation RjDismissFirstRun {
    rj_dismiss_first_run {
      success
      errors
    }
  }
`;

const RjVisibilityFirstRunModal = ({ userId, enabled, onDismiss, dismissedAt }) => {
  const [open, setOpen] = useState(false);
  const [dismissFirstRun] = useMutation(RJ_DISMISS_FIRST_RUN);

  useEffect(() => {
    if (!enabled || !userId) return;
    // Server value wins. A seller who dismissed on another device must not be
    // re-prompted here just because this browser has no localStorage key.
    if (dismissedAt) return;
    try {
      const key = `${STORAGE_KEY_PREFIX}${userId}`;
      if (!localStorage.getItem(key)) {
        setOpen(true);
      }
    } catch (e) {
      // localStorage inaccessible (private mode, SSR, etc.) — silently skip.
    }
  }, [enabled, userId, dismissedAt]);

  const dismiss = (opts = {}) => {
    try {
      localStorage.setItem(`${STORAGE_KEY_PREFIX}${userId}`, new Date().toISOString());
    } catch (e) {
      // ignore
    }
    // Fire-and-forget: the modal closes regardless. A failed write only means
    // this device re-prompts on a different browser — the old behaviour — so it
    // must never block the user from dismissing.
    dismissFirstRun().catch((e) => {
      console.error('[RjVisibilityFirstRun] failed to persist dismissal:', e);
    });
    if (typeof window !== 'undefined' && window.ahoy) {
      window.ahoy.track('rj_visibility_first_run_dismissed', { action: opts.action || 'close' });
    }
    setOpen(false);
    onDismiss && onDismiss(opts);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white border rounded-lg max-w-lg w-full shadow-xl">
        <div className="px-6 py-4 border-b">
          {/* Seller Visibility Feedback 2, item 1 — Annie and Hannah asked for a stronger title and
              CTA, and said they were open to suggestions. Two alternatives were offered alongside
              this one and can be swapped in without touching anything else:
                B — "You're invisible to buyers right now" / "Make me visible"  (most direct)
                C — "Ready to be discovered?"              / "Show me to buyers"
              ⚠️ This is NOT the legally-approved text. RjVisibility/copy.js holds Ashley's verbatim
              disclaimer, where sign-off was waived on condition the reversibility sentence stays
              surfaced. Do not edit that file to "improve the wording". */}
          <h3 className="text-lg font-semibold">Let buyers find you</h3>
        </div>
        <div className="p-6 space-y-4 text-sm text-gray-900 leading-relaxed">
          <p>
            Your firm has enabled <strong>Marketplace Visibility</strong> — an opt-in way for
            growth advisors that have been approved through the site to reach out to you
            directly.
          </p>
          <p>
            When you turn visibility on, buyers in your division can see your profile in
            search and send you a one-time introduction message. You stay in control:
            respond, invite the buyer into a private Meeting Room with an NDA, or end the
            conversation. <strong>You can turn visibility off at any time from your settings.</strong>
          </p>
          <p className="text-gray-500 text-xs">
            Visibility is off by default — your profile is not searchable by others. Open
            Settings when you're ready to opt in.
          </p>
        </div>
        <div className="px-6 py-4 border-t border-succession-border flex justify-end gap-3">
          <Button variant="outline" size="md" onClick={() => dismiss({ action: 'close' })}>
            Not now
          </Button>
          <Button
            as="a"
            variant="primary"
            size="md"
            href="/succession/settings"
            onClick={() => dismiss({ action: 'go_to_settings' })}
            className="no-underline"
          >
            Turn on visibility
          </Button>
        </div>
      </div>
    </div>
  );
};

RjVisibilityFirstRunModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  // Should be true only when: the tenant has rj_seller_visibility on AND
  // the current user is a succession user. Caller handles the compound gate.
  enabled: PropTypes.bool,
  dismissedAt: PropTypes.string,
  onDismiss: PropTypes.func,
};

RjVisibilityFirstRunModal.defaultProps = {
  enabled: false,
};

export default RjVisibilityFirstRunModal;
